import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DateRangeFilter, { DEFAULT_PRESETS, rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import {
  COLORS,
  ChartSkeleton,
  EmptyState,
  ErrorState,
  KpiCard,
} from "../../../components/dashboard/shared/DashboardShared";
import useAuth from "../../../hooks/useAuth";
import useTheme from "../../../hooks/useTheme";
import {
  getCommerceIndicatorsScatter,
  getCommerceIndicatorsSummary,
} from "../../../api/commerceIndicators";
import DataNotices from "./DataNotices";
import DeliveryScatterChart from "./DeliveryScatterChart";
import WeeklyTrendChart from "./WeeklyTrendChart";
import { buildSummarySentences } from "./summaryText";
import {
  formatCount,
  formatHours,
  formatLocalTime,
  formatPct,
  formatYmd,
  minutesToHHmm,
} from "./format";
import "./indicadores.scss";

// Sección "Indicadores" del portal de comercio.
//
// Responde tres preguntas operativas concretas: a qué hora retira el operador,
// a qué hora llegan los pedidos al destinatario y cuántas entregas caen fuera
// del horario de corte.
//
// El backend (microservice-reports) deriva el comercio del JWT y devuelve las
// horas locales YA RESUELTAS. Esta vista NO envía companyId ni timezone y NO
// convierte zonas horarias en ningún punto: ver el encabezado de ./format.js.

// Mismo ajuste que la vista hermana de Informes: "Este mes" (mes completo)
// generaría un endDate futuro, así que se reemplaza por "Este mes hasta hoy".
const INDICATOR_PRESETS = DEFAULT_PRESETS.map((preset) =>
  preset.key === "thisMonth" ? { ...preset, key: "thisMonthToToday" } : preset
);

const DEFAULT_PRESET_KEY = "30d";

const buildInitialRange = () => {
  const [start, end] = rangeFromPreset(DEFAULT_PRESET_KEY);
  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    preset: DEFAULT_PRESET_KEY,
  };
};

/**
 * Contenido de una tarjeta KPI: cifra grande + línea de contexto + nota.
 * KpiCard (compartido) renderiza `value` dentro de su bloque de valor, así que
 * se le pasa este nodo en vez de tocar el componente compartido.
 */
const KpiValue = ({ main, sub, note }) => (
  <>
    <span className="ci-kpi__main">{main}</span>
    {sub ? <span className="ci-kpi__sub">{sub}</span> : null}
    {note ? <span className="ci-kpi__note">{note}</span> : null}
  </>
);

/** Encabezado de una tarjeta de gráfico. */
const ChartCard = ({ title, description, children }) => (
  <section className="ci-card">
    <header className="ci-card__header">
      <h3 className="ci-card__title">{title}</h3>
      {description ? <p className="ci-card__description">{description}</p> : null}
    </header>
    <div className="ci-card__body">{children}</div>
  </section>
);

export default function CommerceIndicators() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();

  // El comercio lo deriva el backend del JWT; el id local solo sirve para
  // recargar si cambia la sesión (cambio de usuario/empresa).
  const companyId = user?.company?._id || null;
  const companyName = user?.company?.name || "";

  const [range, setRange] = useState(buildInitialRange);
  const [summary, setSummary] = useState(null);
  const [scatter, setScatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [scatterError, setScatterError] = useState(null);

  // Guarda contra respuestas fuera de orden: si el usuario cambia el rango dos
  // veces seguidas, la respuesta vieja no debe pisar a la nueva.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    setSummaryError(null);
    setScatterError(null);

    const params = { startDate: range.start, endDate: range.end };
    const [summaryResult, scatterResult] = await Promise.all([
      getCommerceIndicatorsSummary(params),
      getCommerceIndicatorsScatter(params),
    ]);

    if (requestIdRef.current !== requestId) return;

    if (summaryResult?.error) {
      setSummary(null);
      setSummaryError(summaryResult.error.message);
    } else {
      setSummary(summaryResult);
    }

    if (scatterResult?.error) {
      setScatter(null);
      setScatterError(scatterResult.error.message);
    } else {
      setScatter(scatterResult);
    }

    setLoading(false);
    // companyId no viaja en la petición (lo pone el JWT), pero un cambio de
    // sesión cambia los datos: por eso está en las dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRangeChange = (start, end, preset) => {
    setRange({ start, end, preset });
  };

  const collection = summary?.collection || null;
  const delivery = summary?.delivery || null;
  const leadTime = summary?.leadTime || null;
  const failed = summary?.failed || null;
  const onTime = summary?.onTime || null;
  const cutoffHour = summary?.thresholds?.deliveryCutoffHour;
  const cutoffLabel = minutesToHHmm(typeof cutoffHour === "number" ? cutoffHour * 60 : null);

  const sentences = useMemo(() => buildSummarySentences(summary, t), [summary, t]);

  const topReason = failed?.topReasons?.[0] || null;

  const hasAnyData =
    Boolean(summary) && ((collection?.count || 0) > 0 || (delivery?.count || 0) > 0);

  const scatterBuckets = scatter?.buckets || [];
  const weekly = summary?.weekly || [];

  const percentileSubtitle = (p10, p90) =>
    p10 && p90 ? t("Entre {{p10}} y {{p90}} (8 de cada 10 casos)", { p10, p90 }) : null;

  return (
    <div className="w-100 all-orders-container mt-6 ci-page">
      <div className="ci-header">
        <h2 className="ci-title">{t("Indicadores")}</h2>
        <p className="ci-subtitle">
          {companyName
            ? t("Horarios reales de retiro y entrega de los pedidos de {{empresa}}.", { empresa: companyName })
            : t("Horarios reales de retiro y entrega de tus pedidos.")}
        </p>
      </div>

      <div className="ci-toolbar">
        <DateRangeFilter
          compact
          value={range.preset}
          presets={INDICATOR_PRESETS}
          onChange={handleRangeChange}
        />
      </div>

      {summaryError ? (
        <div className="ci-error">
          <ErrorState onRetry={load} />
          <p className="ci-error__detail">{summaryError}</p>
        </div>
      ) : (
        <>
          <DataNotices
            range={summary?.range || null}
            delivery={delivery}
            scatterTruncated={Boolean(scatter?.truncated)}
          />

          {!loading && !hasAnyData ? (
            <EmptyState
              icon="fas fa-chart-line"
              title={t("Todavía no hay indicadores para este período")}
              subtitle={t("No registramos retiros ni entregas en el rango seleccionado. Probá con un rango más amplio.")}
            />
          ) : (
            <>
              {/* Fila 1 — las tres preguntas */}
              <div className="ci-kpi-grid">
                <KpiCard
                  loading={loading}
                  title={t("¿A qué hora retira mi operador?")}
                  color={COLORS.info}
                  icon="fas fa-truck"
                  tooltip={t("Hora típica (mediana) en que se retiran tus pedidos, en la hora local de tu operación.")}
                  value={
                    <KpiValue
                      main={formatLocalTime(collection?.medianLocalTime)}
                      sub={percentileSubtitle(collection?.p10LocalTime, collection?.p90LocalTime)}
                      note={
                        collection?.warehouseReceptionsExcluded > 0
                          ? t("No se cuentan {{n}} recepciones en bodega.", {
                              n: formatCount(collection.warehouseReceptionsExcluded),
                            })
                          : null
                      }
                    />
                  }
                />

                <KpiCard
                  loading={loading}
                  title={t("¿A qué hora se entregan mis pedidos?")}
                  color={COLORS.success}
                  icon="fas fa-box-open"
                  tooltip={t("Hora típica (mediana) de entrega al destinatario, en la hora local de tu operación.")}
                  value={
                    <KpiValue
                      main={formatLocalTime(delivery?.medianLocalTime)}
                      sub={
                        delivery?.p90LocalTime
                          ? t("9 de cada 10 antes de las {{p90}}", { p90: delivery.p90LocalTime })
                          : null
                      }
                      note={
                        delivery?.count
                          ? t("Sobre {{n}} entregas.", { n: formatCount(delivery.count) })
                          : null
                      }
                    />
                  }
                />

                <KpiCard
                  loading={loading}
                  title={t("Entregas después de las {{corte}}", { corte: cutoffLabel })}
                  color={COLORS.danger}
                  icon="fas fa-moon"
                  tooltip={t("Porcentaje de entregas realizadas pasado el horario de corte.")}
                  value={
                    <KpiValue
                      main={formatPct(delivery?.afterCutoff?.pct)}
                      sub={
                        delivery?.count
                          ? t("{{n}} de {{total}} entregas", {
                              n: formatCount(delivery?.afterCutoff?.count),
                              total: formatCount(delivery.count),
                            })
                          : null
                      }
                    />
                  }
                />
              </div>

              {/* Fila 2 — operación básica */}
              <div className="ci-kpi-grid">
                <KpiCard
                  loading={loading}
                  title={t("Mismo día antes de las {{corte}}", { corte: cutoffLabel })}
                  color={COLORS.primary}
                  icon="fas fa-bullseye"
                  tooltip={t("Pedidos entregados el mismo día en que fueron retirados, antes del horario de corte.")}
                  value={
                    <KpiValue
                      main={formatPct(onTime?.sameDayBeforeCutoffPct)}
                      sub={
                        onTime?.eligible
                          ? t("{{matched}} de {{eligible}} pedidos evaluados", {
                              matched: formatCount(onTime.matched),
                              eligible: formatCount(onTime.eligible),
                            })
                          : null
                      }
                    />
                  }
                />

                <KpiCard
                  loading={loading}
                  title={t("Tiempo hasta la entrega")}
                  color={COLORS.warning}
                  icon="fas fa-hourglass-half"
                  tooltip={t("Horas transcurridas entre el retiro y la entrega (mediana).")}
                  value={
                    <KpiValue
                      main={formatHours(leadTime?.medianHours)}
                      sub={
                        leadTime?.p90Hours !== null && leadTime?.p90Hours !== undefined
                          ? t("9 de cada 10 en menos de {{p90}}", { p90: formatHours(leadTime.p90Hours) })
                          : null
                      }
                      note={
                        leadTime?.count
                          ? t("Sobre {{n}} pedidos.", { n: formatCount(leadTime.count) })
                          : null
                      }
                    />
                  }
                />

                <KpiCard
                  loading={loading}
                  title={t("Intentos de entrega fallidos")}
                  color={COLORS.secondary}
                  icon="fas fa-exclamation-triangle"
                  tooltip={t("Intentos de entrega que no se concretaron y su motivo más frecuente.")}
                  value={
                    <KpiValue
                      main={formatCount(failed?.attempts)}
                      sub={
                        topReason
                          ? t("Motivo más frecuente: {{motivo}} ({{n}})", {
                              motivo: topReason.reason,
                              n: formatCount(topReason.count),
                            })
                          : null
                      }
                      note={
                        failed?.packages
                          ? t("{{n}} pedidos afectados.", { n: formatCount(failed.packages) })
                          : null
                      }
                    />
                  }
                />
              </div>

              {/* Gráfico principal */}
              <ChartCard
                title={t("Cuándo se entregan tus pedidos")}
                description={t(
                  "Cada punto es un grupo de entregas del mismo día y horario; mientras más grande, más entregas. La línea roja marca el corte de las {{corte}}.",
                  { corte: cutoffLabel }
                )}
              >
                {loading ? (
                  <ChartSkeleton height={420} />
                ) : scatterError ? (
                  <div className="ci-inline-error" role="alert">
                    <p className="ci-inline-error__text">{scatterError}</p>
                    <button type="button" className="ci-retry-btn" onClick={load}>
                      {t("Reintentar")}
                    </button>
                  </div>
                ) : scatterBuckets.length === 0 ? (
                  <EmptyState
                    title={t("Sin entregas con hora registrada")}
                    subtitle={t("No hay eventos con horario en el rango seleccionado.")}
                  />
                ) : (
                  <DeliveryScatterChart
                    buckets={scatterBuckets}
                    operators={scatter?.operators || []}
                    cutoffHour={scatter?.thresholds?.deliveryCutoffHour ?? cutoffHour}
                    isDark={isDark}
                    // El eje X cubre el período completo para que los días sin
                    // entregas se vean como huecos reales. Se usa el rango que
                    // devolvió el backend, no el que pidió el usuario: puede haber
                    // sido recortado por `reliableFrom`.
                    rangeStart={scatter?.range?.startDate}
                    rangeEnd={scatter?.range?.endDate}
                    truncated={Boolean(scatter?.truncated)}
                  />
                )}
              </ChartCard>

              {/* Gráfico secundario */}
              <ChartCard
                title={t("Tendencia semanal")}
                description={t("Entregas por semana y cuántas de ellas quedaron después del corte.")}
              >
                {loading ? (
                  <ChartSkeleton height={280} />
                ) : weekly.length === 0 ? (
                  <EmptyState
                    title={t("Sin semanas para comparar")}
                    subtitle={t("El período seleccionado no alcanza a cubrir una semana con entregas.")}
                  />
                ) : (
                  <WeeklyTrendChart weekly={weekly} cutoffHour={cutoffHour} isDark={isDark} />
                )}
              </ChartCard>

              {/* Resumen en texto */}
              {!loading && sentences.length > 0 && (
                <section className="ci-card ci-summary">
                  <header className="ci-card__header">
                    <h3 className="ci-card__title">{t("En resumen")}</h3>
                  </header>
                  <div className="ci-card__body">
                    <ul className="ci-summary__list">
                      {sentences.map((sentence) => (
                        <li key={sentence} className="ci-summary__item">
                          {sentence}
                        </li>
                      ))}
                    </ul>
                    {summary?.range?.startDate && summary?.range?.endDate && (
                      <p className="ci-summary__footnote">
                        {t("Datos del {{inicio}} al {{fin}}.", {
                          inicio: formatYmd(summary.range.startDate),
                          fin: formatYmd(summary.range.endDate),
                        })}
                        {summary?.range?.timeZone
                          ? ` ${t("Horas en {{zona}}.", { zona: summary.range.timeZone })}`
                          : null}
                      </p>
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
